import { useEffect, useRef } from "react";
import useDash from "../components/Dashboard/v2/useDash";
import { getExpiryHints } from "../api";
import { useCompany } from "../Context/CompanyContext";

// Everything that can change an item's expiry picture: stock movements (sales, purchases,
// adjustments and returns all write ledger rows), the documents, the items themselves and
// the store settings (near-expiry days, the Batch & Expiry switch).
const EMPTY = {};
const LIVE = ["stock", "items", "categories", "purchases", "invoices", "stockAdjustments", "returns", "company"];

/**
 * Expiry hints for the POS item cards: { [itemId]: { status, batchCount, totalQty, unit, expired, near } }
 * for the items that have expired stock, stock close to expiry, or several batches - from
 * GET /stocks/expiry-hints (small, and only fetched while Batch & Expiry is on).
 * Refreshes by itself when stock changes, after midnight, and when the tab is shown again. An
 * entry whose content did not change keeps the SAME object, so memoised cards do not re-render.
 */
export default function useExpiryHints() {
  const { company } = useCompany();
  // Hints are needed for the card tags (two store switches) AND for deciding whether a card click
  // opens the batch chooser (Settings -> "Which batch is sold at the till" is not Automatic).
  const feature = company?.batchExpiryEnabled === true;
  const showExpiry = feature && company?.showExpiryOnCards !== false;
  const showStock = feature && company?.showStockTagsOnCards !== false;
  const pickMode = feature ? company?.batchPickMode || "auto" : "auto";
  const enabled = showExpiry || showStock || pickMode !== "auto";
  const list = useDash("expiry-hints", {}, {
    enabled,
    fetcher: () => getExpiryHints(),
    live: LIVE,
    liveMs: 3000,
    midnight: true,
    refreshOnFocus: true,
  });

  const prev = useRef({});
  const next = enabled && list.data?.items ? list.data.items : null;
  let hints = prev.current;
  if (!enabled) {
    hints = EMPTY;
  } else if (next) {
    hints = {};
    Object.keys(next).forEach((id) => {
      const old = prev.current[id];
      hints[id] = old && JSON.stringify(old) === JSON.stringify(next[id]) ? old : next[id];
    });
  }
  useEffect(() => {
    prev.current = hints;
  });

  return { enabled, hints, showExpiry, showStock, pickMode, nearExpiryDays: list.data?.nearExpiryDays };
}
